import { useForm } from "react-hook-form";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AccountResponse } from "../types/account.types";
import {
  createDirectTransfer,
  getSupportedTransferModes,
  type TransferAccountDto,
  type CreateDirectTransferResponse,
} from "./direct-transfer.service";
import { getAccounts } from "../services/get-accounts.service";

export type TransferStep = "details" | "confirm" | "processing" | "result" | "error";

export interface DirectTransferFormData {
  originAccount: string | null;
  amount: string;
  destinationAccount: string | null;
  description: string;
  transferMode: string;
}

const getDefaultValues = (): DirectTransferFormData => ({
  originAccount: null,
  amount: "",
  destinationAccount: null,
  description: "",
  transferMode: "PIX",
});

const MIN_PROCESSING_MS = 2500;

export const useDirectTransferForm = (initialFiscalNumber?: string) => {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<TransferStep>("details");
  const [originFiscalNumber, setOriginFiscalNumber] = useState<string | undefined>(initialFiscalNumber);
  const [originAccountData, setOriginAccountData] = useState<AccountResponse | null>(null);
  const [destinationAccountData, setDestinationAccountData] = useState<AccountResponse | null>(null);
  const [transferResult, setTransferResult] = useState<CreateDirectTransferResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Track pending result while processing animation runs
  const pendingResult = useRef<{ type: "success" | "error"; data?: CreateDirectTransferResponse; errorMsg?: string } | null>(null);
  const processingStart = useRef<number>(0);

  const form = useForm<DirectTransferFormData>({
    defaultValues: getDefaultValues(),
    mode: "onBlur",
  });

  const originInstitutionName = originAccountData?.institution.name;

  const { data: transferModes = [], isLoading: _isLoadingModes } = useQuery({
    queryKey: ["cashPooling", "transferModes", originInstitutionName],
    queryFn: () => getSupportedTransferModes(originInstitutionName!),
    enabled: !!originInstitutionName,
  });

  // Prefetch destination accounts when origin is selected
  const originOwnerFiscalNumber = originAccountData?.owner.fiscalNumber;
  useEffect(() => {
    if (originOwnerFiscalNumber) {
      queryClient.prefetchQuery({
        queryKey: ["cashPooling", "accounts", { type: "DESTINATION" as const, fiscalNumber: originOwnerFiscalNumber }],
        queryFn: () => getAccounts({ type: "DESTINATION", fiscalNumber: originOwnerFiscalNumber }),
      });
    }
  }, [originOwnerFiscalNumber, queryClient]);

  useEffect(() => {
    if (!originInstitutionName) return;
    if (transferModes.includes("PIX")) {
      form.setValue("transferMode", "PIX");
    } else if (transferModes.length === 1) {
      form.setValue("transferMode", transferModes[0]);
    } else if (transferModes.length > 1) {
      form.setValue("transferMode", "");
    }
  }, [transferModes, originInstitutionName, form]);

  const finishProcessing = useCallback((type: "success" | "error", data?: CreateDirectTransferResponse, errorMsg?: string) => {
    const elapsed = Date.now() - processingStart.current;
    const remaining = Math.max(0, MIN_PROCESSING_MS - elapsed);

    const applyResult = () => {
      if (type === "success" && data) {
        queryClient.invalidateQueries({ queryKey: ["cashPooling", "clientSummary"] });
        queryClient.invalidateQueries({ queryKey: ["cashPooling", "transferHistory"] });
        setTransferResult(data);
        setErrorMessage(null);
        setStep("result");
      } else {
        setErrorMessage(errorMsg || null);
        setStep("error");
      }
      pendingResult.current = null;
    };

    if (remaining > 0) {
      pendingResult.current = { type, data, errorMsg };
      setTimeout(applyResult, remaining);
    } else {
      applyResult();
    }
  }, [queryClient]);

  const mutation = useMutation({
    mutationFn: createDirectTransfer,
    onSuccess: (data) => {
      if (data.status === "FAILED") {
        finishProcessing("error");
      } else {
        finishProcessing("success", data);
      }
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || error?.message || null;
      finishProcessing("error", undefined, msg);
    },
  });

  const handleOriginAccountChange = useCallback(
    (account: AccountResponse | AccountResponse[] | null) => {
      const singleAccount = Array.isArray(account) ? account[0] ?? null : account;
      setOriginAccountData(singleAccount);
      form.setValue("destinationAccount", null);
      setDestinationAccountData(null);

      if (singleAccount) {
        setOriginFiscalNumber(singleAccount.owner.fiscalNumber);
      } else {
        setOriginFiscalNumber(initialFiscalNumber);
      }
    },
    [initialFiscalNumber, form],
  );

  const handleDestinationAccountChange = useCallback(
    (account: AccountResponse | AccountResponse[] | null) => {
      const singleAccount = Array.isArray(account) ? account[0] ?? null : account;
      setDestinationAccountData(singleAccount);
    },
    [],
  );

  const mapAccountToDto = (account: AccountResponse): TransferAccountDto => ({
    accountId: account.account.accountId,
    institutionName: account.institution.name,
    branch: account.account.branch,
    accountNumber: account.account.accountNumber,
    fiscalName: account.owner.fiscalName,
    fiscalNumber: account.owner.fiscalNumber,
  });

  const onContinue = form.handleSubmit(() => {
    setStep("confirm");
  });

  const onConfirm = () => {
    processingStart.current = Date.now();
    setStep("processing");
    const data = form.getValues();
    mutation.mutate({
      originAccount: mapAccountToDto(originAccountData!),
      destinationAccount: mapAccountToDto(destinationAccountData!),
      amount: parseFloat(data.amount),
      description: data.description || undefined,
      transferMode: data.transferMode,
    });
  };

  const onBackToDetails = () => {
    setStep("details");
  };

  const onTryAgain = () => {
    setStep("confirm");
  };

  const onNewTransfer = () => {
    form.reset(getDefaultValues());
    setOriginAccountData(null);
    setDestinationAccountData(null);
    setOriginFiscalNumber(initialFiscalNumber);
    setTransferResult(null);
    setErrorMessage(null);
    pendingResult.current = null;
    setStep("details");
  };

  return {
    form,
    step,
    onContinue,
    onConfirm,
    onBackToDetails,
    onNewTransfer,
    onTryAgain,
    isSubmitting: mutation.isPending,
    handleOriginAccountChange,
    handleDestinationAccountChange,
    originFiscalNumber,
    originAccountData,
    destinationAccountData,
    transferResult,
    transferModes,
    errorMessage,
  };
};
